import { apiFetch, apiFetchWithMeta } from '../../common/api/apiFetchClient';
import { isAllSaversPortal } from '../../auth/AuthContext';

const HOME_SEARCH_TIMEOUT_MESSAGE =
  'Search request timed out because too many results may have matched. Please enter a more specific search term and try again.';

function isAbortError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

function createTimedOutSearchError(traceId?: string): Error & { traceId?: string } {
  const error = new Error(HOME_SEARCH_TIMEOUT_MESSAGE) as Error & { traceId?: string };
  error.name = 'HomeSearchTimeoutError';
  error.traceId = traceId;
  return error;
}

function readTraceIdFromError(error: unknown): string | undefined {
  return typeof error === 'object' && error !== null && 'traceId' in error && typeof error.traceId === 'string'
    ? error.traceId
    : undefined;
}

export interface PolicySummary {
  surveyId: number | null;
  employerGroupName: string | null;
  employerGroupEIN: string | null;
  policyNumber: string | null;
  medIdCardNum: string | null;
  fundingType: string | null;
  surveyStatus: string | null;
  surveyYear: string | null;
  customerNumber: string | null;
  surveyStartDate: string | null;
  surveyCompletedDate: string | null;
  userName: string | null;
}

export interface ActiveSurveySummary {
  createdDate: string | null;
  modifiedDate: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
  surveyId: number;
  name: string | null;
  description: string | null;
  startDate: string | null;
  active: boolean;
  endDate: string | null;
}

export interface StartSurveyDecision {
  allowed: boolean;
  requiresReopenConfirmation: boolean;
  reasonCode:
    | 'ACTIVE_SURVEY_UNAVAILABLE'
    | 'REOPEN_CONFIRMATION_REQUIRED'
    | null;
  message: string | null;
  navigationPath: string | null;
  activeSurvey: ActiveSurveySummary | null;
  selectedCustomer: {
    customerNumber: string;
    employerGroupName: string;
    employerGroupEIN: string | null;
    policyNumber: string;
    policyList: string;
    fundingType: string;
    surveyStatus: string;
    surveyYear: string;
    surveyId: number | null;
  };
}

export async function searchPoliciesBySearchId(args: {
  searchId: string;
  origin?: string;
}): Promise<{ data: PolicySummary[]; traceId: string }> {
  const searchId = args.searchId.trim();

  const query = new URLSearchParams();
  if (args.origin) {
    query.set('origin', args.origin);
  }

  const querySuffix = query.toString() ? `?${query.toString()}` : '';
  try {
    return await apiFetchWithMeta<PolicySummary[]>(
      `/api/rfi/policy/search/${encodeURIComponent(searchId)}${querySuffix}`,
      {
        method: 'GET',
        timeoutMs: 15_000,
      }
    );
  } catch (error) {
    if (isAbortError(error)) {
      throw createTimedOutSearchError(readTraceIdFromError(error));
    }
    throw error;
  }
}

function resolveExternalCurrentEndpoint(portalName: string) {
  if (isAllSaversPortal(portalName)) return '/api/rfi/policy/current/allsavers';
  if (portalName === 'UHCeServices') return '/api/rfi/policy/current/uhces';
  return '/api/rfi/policy/current/ees';
}

export async function searchPoliciesByPolicyIds(args: {
  policyIds: string[];
  portal?: string;
}): Promise<{ data: PolicySummary[]; traceId: string }> {
  const policyIds = args.policyIds
    .map((id) => {return id.trim()})
    .filter(Boolean);

  if (!policyIds.length) {
    return { data: [], traceId: '' };
  }

  const portalName = (args.portal ?? '').trim();
  const externalCurrentEndpoint = resolveExternalCurrentEndpoint(portalName);

  return apiFetchWithMeta<PolicySummary[]>(externalCurrentEndpoint, {
    method: 'POST',
    body: JSON.stringify({ policyIds }),
    timeoutMs: 15_000,
  });
}

export async function startSurveySession(args: {
  customerNumber: string;
  employerGroupName: string;
  employerGroupEIN: string | null;
  policyNumber: string;
  policyList: string;
  fundingType: string;
  surveyStatus: string;
  surveyYear: string;
  confirmReopen?: boolean;
}): Promise<StartSurveyDecision> {
  return apiFetch<StartSurveyDecision>(
    '/api/rfi/survey/start',
    {
      method: 'POST',
      body: JSON.stringify({
        customerNumber: args.customerNumber,
        employerGroupName: args.employerGroupName,
        employerGroupEIN: args.employerGroupEIN,
        policyNumber: args.policyNumber,
        policyList: args.policyList,
        fundingType: args.fundingType,
        surveyStatus: args.surveyStatus,
        surveyYear: args.surveyYear,
        confirmReopen: args.confirmReopen === true,
      }),
    }
  );
}
